import React, { useState } from "react";
import { X, Send, Loader2, Bell } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { CustomerCombobox } from "./CustomerCombobox";

const STATUS_LABELS = {
  scheduled: "Booked",
  en_route: "En route",
  on_site: "On site",
  completed_pending_review: "Completed (pending review)",
  completed: "Completed",
  invoiced: "Invoiced",
};

/**
 * Send Status to Client — fires a friendly email + magic-link portal so
 * the client always knows what stage the job is at. The template picks
 * automatically from the job's current status.
 */
export const NotifyStatusModal = ({ job, onClose, onSent }) => {
  const status = (job.status || "scheduled").toLowerCase();
  const label = STATUS_LABELS[status] || status;

  const [to, setTo] = useState(job.contact_email || "");
  const [message, setMessage] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (!/@/.test(to)) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.notifyStatus(job.id, {
        recipient_email: to.trim(),
        message: message.trim() || null,
      });
      if (res.ok) {
        toast.success(
          `Status update sent to ${res.sent_to}` + (res.used_sandbox ? " · from Resend sandbox" : ""),
        );
        onSent && onSent(res);
        onClose();
      } else {
        toast.error(res.error || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      data-testid="notify-status-modal"
    >
      <div className="bg-white rounded-md shadow-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#f28a16]">
              Send status to client
            </div>
            <h2 className="font-display font-black uppercase text-xl text-[#111619] mt-1">
              Notify client
            </h2>
            <p className="text-xs text-[#3f4b52] mt-1">
              Current status:{" "}
              <b className="text-[#111619]">{label}</b> · Job #{job.job_number}
            </p>
          </div>
          <button onClick={onClose} className="text-[#3f4b52]" aria-label="Close" data-testid="notify-close-btn">
            <X size={18} />
          </button>
        </div>

        <div className="mt-5 space-y-4">
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Look up customer
            </label>
            <div className="mt-1">
              <CustomerCombobox
                value={picked}
                onChange={(c) => {
                  setPicked(c);
                  if (c && c.email) setTo(c.email);
                }}
                testid="notify-customer-picker"
                placeholder="Type name or code to auto-fill To…"
              />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              To (client email)
            </label>
            <input
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="client@example.com"
              className="w-full mt-1 h-10 px-3 border border-[#cfd7db] rounded-md text-sm"
              data-testid="notify-to-input"
            />
          </div>
          <div>
            <label className="text-xs uppercase font-display tracking-widest text-[#3f4b52]">
              Extra message (optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              placeholder="Any extra detail you'd like to add…"
              className="w-full mt-1 px-3 py-2 border border-[#cfd7db] rounded-md text-sm"
              data-testid="notify-message-input"
            />
          </div>

          <div className="text-[11px] text-[#3f4b52] bg-[#f5f6f6] border border-[#cfd7db] rounded-md p-3">
            <Bell size={12} className="inline mr-1 -mt-0.5 text-[#f28a16]" />
            A stage-appropriate email is generated automatically and includes a
            secure magic-link to the client portal so they can see documents
            and details — no login required.
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="border border-[#cfd7db] text-[#3f4b52] font-display uppercase tracking-widest text-xs px-4 h-10 rounded-md hover:bg-[#f5f6f6]"
            data-testid="notify-cancel-btn"
          >
            Cancel
          </button>
          <button
            onClick={send}
            disabled={busy}
            data-testid="notify-send-btn"
            className="bg-[#f28a16] text-white font-display uppercase tracking-widest text-xs px-5 h-10 rounded-md inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            {busy ? "Sending…" : "Send update"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotifyStatusModal;
